import { useState } from "react";

import { TextToolState } from "@impero-com/ui";
import { Row } from "@tanstack/react-table";

import { useUpdateControlResultAssignmentsForUserMutation } from "org/user/queries/useUpdateControlResultAssignmentsForUserMutation";
import { UserOrUserGroupOverview } from "utils/user";

import { buildPatchControlResultsBody } from "./buildPatchControlResultsBody";
import { ControlResultRow } from "./useUserControlResultListTable";

interface UseReplaceControlResultUserUtilsProps {
    userId: number;
    selectedRows: Row<ControlResultRow>[];
    deselectAll: () => void;
}

export function useReplaceControlResultUserUtils({
    userId,
    selectedRows,
    deselectAll,
}: UseReplaceControlResultUserUtilsProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { mutate, isPending, error, reset } =
        useUpdateControlResultAssignmentsForUserMutation();

    const replaceUserToolState: TextToolState =
        selectedRows.length === 0 ? { type: "DISABLED" } : { type: "ENABLED" };

    const replaceUser = (userOrGroup: UserOrUserGroupOverview) => {
        mutate(
            {
                userId,
                data: {
                    newUserId: userOrGroup.id,
                    controlResultSteps:
                        buildPatchControlResultsBody(selectedRows),
                },
            },
            {
                onSuccess: () => {
                    deselectAll();
                    setIsModalOpen(false);
                },
                // On failure the modal stays open; `error` drives the notice.
            }
        );
    };

    return {
        replaceUserToolState,
        isReplaceUserModalOpen: isModalOpen,
        replaceUserError: error,
        // Reset on open (not close) so the notice stays put during the close
        // animation and a stale error can't reappear next time.
        openReplaceUserModal: () => {
            reset();
            setIsModalOpen(true);
        },
        closeReplaceUserModal: () => setIsModalOpen(false),
        replaceUser,
        isReplacingUser: isPending,
    };
}
